import React from 'react';
import { active } from '@curi/interactions';
var key = {
  response: null,
  navigation: null
};
var responseContext = React.createContext(key);
var ResponseProvider = responseContext.Provider,
  ResponseConsumer = responseContext.Consumer;
var routerContext = React.createContext(null);
var RouterProvider = routerContext.Provider,
  RouterConsumer = routerContext.Consumer;
function createRouterComponent(router) {
  function initialState() {
    return router.current();
  }
  return function Router(props) {
    var _a = React.useState(initialState),
      response = _a[0],
      setResponse = _a[1];
    React.useEffect(function () {
      var removed = false;
      var stop = router.observe(function (_a) {
        var response = _a.response,
          navigation = _a.navigation;
        if (!removed) {
          setResponse({
            response: response,
            navigation: navigation
          });
        }
      }, {
        initial: false
      });
      return function () {
        removed = true;
        stop();
      };
    }, []);
    return React.createElement(RouterProvider, {
      value: router
    }, React.createElement(ResponseProvider, {
      value: response
    }, props.children));
  };
}
function useRouter() {
  return React.useContext(routerContext);
}
function useResponse() {
  return React.useContext(responseContext);
}
function useActive(props) {
  var router = useRouter();
  var response = useResponse().response;
  var route = router.route(props.name);
  if (!route) {
    return false;
  }
  return active(route, response, {
    params: props.params,
    partial: props.partial,
    components: props.components
  });
}
function useURL(props) {
  var router = useRouter();
  return router.url(props);
}
function useNavigating() {
  var router = useRouter();
  var _a = React.useState(undefined),
    cancel = _a[0],
    setCancel = _a[1];
  React.useEffect(function () {
    var removed = false;
    var stop = router.cancel(function (callback) {
      if (!removed) {
        setCancel(function () {
          return callback;
        });
      }
    });
    return function () {
      removed = true;
      stop();
    };
  }, []);
  return cancel;
}
function defaultCanNavigate() {
  return true;
}
function useNavigationHandler(props) {
  var url = props.url,
    onNav = props.onNav,
    method = props.method,
    target = props.target,
    state = props.state,
    _a = props.canNavigate,
    canNavigate = _a === void 0 ? defaultCanNavigate : _a;
  var router = useRouter();
  var eventHandler = React.useCallback(function eventHandler(event) {
    if (onNav) {
      onNav(event);
    }
    if (canNavigate(event, target)) {
      event.preventDefault();
      router.navigate({
        url: url,
        state: state,
        method: method
      });
    }
  }, [url, method, state, onNav, target]);
  return {
    url: url,
    eventHandler: eventHandler
  };
}
function useStatefulNavigationHandler(props) {
  var url = props.url,
    onNav = props.onNav,
    method = props.method,
    target = props.target,
    state = props.state,
    _a = props.canNavigate,
    canNavigate = _a === void 0 ? defaultCanNavigate : _a;
  var router = useRouter();
  var removeCallbacks = React.useRef(undefined);
  var _b = React.useState(false),
    navigating = _b[0],
    setNavigating = _b[1];
  React.useEffect(function () {
    return function () {
      if (removeCallbacks.current) {
        removeCallbacks.current();
      }
    };
  }, []);
  var eventHandler = React.useCallback(function eventHandler(event) {
    if (onNav) {
      onNav(event);
    }
    if (canNavigate(event, target)) {
      event.preventDefault();
      var done = function done() {
        removeCallbacks.current = undefined;
        setNavigating(false);
      };
      setNavigating(true);
      removeCallbacks.current = router.navigate({
        url: url,
        state: state,
        method: method,
        cancelled: done,
        finished: done
      });
    }
  }, [url, method, state, onNav, target]);
  return {
    url: url,
    eventHandler: eventHandler,
    navigating: navigating
  };
}
function useConfirm(fn) {
  var router = useRouter();
  React.useEffect(function () {
    router.history.confirm(fn);
    return function () {
      router.history.confirm();
    };
  }, [fn]);
}
export { ResponseConsumer, RouterConsumer, createRouterComponent, useActive, useConfirm, useNavigating, useNavigationHandler, useResponse, useRouter, useStatefulNavigationHandler, useURL };